import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { StatusBadge } from '@/components/sales/status-badge';
import { VoidDialog } from '@/components/sales/void-dialog';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatMinor, toMinor } from '@/lib/money';
import { exponentOf, rateLine } from '@/lib/currencies';
import { formatAmount, formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { ltrIn, paymentMethodLabel, usePayment, type Payment } from '@/lib/sales';
import { useAccountNames } from '@/lib/accounts';

export const Route = createFileRoute('/sales/payments/$paymentId')({ component: View });

/** A customer payment (S7), read-only, with void. */
function View() {
  const t = useT();
  const names = useAccountNames();
  const { paymentId } = Route.useParams();
  const orgId = useOrgId();
  const p = usePayment(paymentId);
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [voiding, setVoiding] = useState(false);
  const voidM = useMutation({
    mutationFn: ({ date, reason }: { date: string; reason: string }) =>
      api<Payment>(`/orgs/${orgId}/sales/payments/${paymentId}/void`, {
        method: 'POST',
        body: JSON.stringify({ date, reason }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      toast(t('salesPages.paymentView.voided', { number: ltrIn(r.number) }));
      setVoiding(false);
    },
  });
  const close = () => void navigate({ to: '/sales/all' });
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  if (p.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (p.isError)
    return <EmptyState title={t('salesPages.paymentView.notFound')} hint={p.error.message} />;
  const d = p.data;
  // multi-currency: amounts are in the payment's currency (migration 0900)
  const exp = exponentOf(d.currency);
  const applied = d.allocations.reduce((s, a) => s + (toMinor(a.amount, exp) ?? 0n), 0n);
  return (
    <OverlayPage
      title={t('salesPages.paymentView.title', { number: ltrIn(d.number) })}
      subtitle={<StatusBadge kind="payment" status={d.status} overdue={false} />}
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          {t('common.close')}
        </Button>
      }
      footerCentre={
        <Button variant="ghost" asChild>
          <Link to="/accounting/journal-entries/$entryId" params={{ entryId: d.journalEntryId }}>
            {t('salesPages.paymentView.transactionJournal')}
          </Link>
        </Button>
      }
      footerRight={
        d.status === 'posted' && me.data?.isStaff ? (
          <Button variant="destructive" onClick={() => setVoiding(true)}>
            {t('salesPages.document.void')}
          </Button>
        ) : null
      }
    >
      <div className="mx-auto flex max-w-[1000px] flex-col gap-5">
        {d.status === 'void' ? (
          <div className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
            {d.voidReason
              ? t('salesPages.paymentView.voidNoticeReason', { reason: d.voidReason })
              : t('salesPages.paymentView.voidNotice')}
          </div>
        ) : null}
        <dl className="grid grid-cols-[200px_1fr] gap-x-6 gap-y-2 rounded-lg border bg-card p-5 text-sm">
          <dt className="text-muted-foreground">{t('salesPages.paymentView.customer')}</dt>
          <dd>
            <Link
              to="/sales/customers/$contactId"
              params={{ contactId: d.contactId }}
              className="text-primary hover:underline"
            >
              {d.contactName}
            </Link>
          </dd>
          <dt className="text-muted-foreground">{t('salesPages.paymentView.paymentDate')}</dt>
          <dd>{formatDate(d.date)}</dd>
          <dt className="text-muted-foreground">{t('salesPages.paymentView.paymentMethod')}</dt>
          <dd>{paymentMethodLabel(d.method) || '—'}</dd>
          <dt className="text-muted-foreground">{t('salesPages.paymentView.referenceNo')}</dt>
          <dd>{d.reference ?? '—'}</dd>
          <dt className="text-muted-foreground">{t('salesPages.paymentView.depositTo')}</dt>
          <dd>{names.codeName(d.depositAccountId, d.depositAccountName)}</dd>
          <dt className="text-muted-foreground">{t('salesPages.paymentView.amountReceived')}</dt>
          <dd className="tnum font-semibold">
            {d.currency} {formatAmount(d.amount)}
          </dd>
          {d.currency !== d.homeCurrency ? (
            <>
              <dt className="text-muted-foreground">{t('salesPages.paymentView.exchangeRate')}</dt>
              <dd className="tnum">{rateLine(d.currency, d.fxRate, d.homeCurrency)}</dd>
              <dt className="text-muted-foreground">
                {t('salesPages.paymentView.amountReceivedIn', { currency: d.homeCurrency })}
              </dt>
              <dd className="tnum">
                {d.homeCurrency} {formatAmount(d.homeAmount)}
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">{t('salesPages.paymentView.unapplied')}</dt>
          <dd className="tnum">
            {d.currency} {formatAmount(d.unapplied)}
          </dd>
          <dt className="text-muted-foreground">{t('salesPages.paymentView.memo')}</dt>
          <dd>{d.memo ?? '—'}</dd>
        </dl>
        <section className="rounded-lg border bg-card">
          <h2 className="border-b px-4 py-2 text-sm font-semibold">
            {t('salesPages.paymentView.appliedTo')}
          </h2>
          {d.allocations.length ? (
            <ul className="divide-y text-sm">
              {d.allocations.map((a) => (
                <li key={a.id} className="flex justify-between px-4 py-2">
                  {a.type === 'journal_entry' ? (
                    <span>
                      {t('salesPages.paymentView.journalEntry', { number: ltrIn(a.refNumber) })}
                    </span>
                  ) : (
                    <Link
                      to={`/sales/invoices/${a.refId}`}
                      className="text-primary hover:underline"
                    >
                      {t('salesPages.paymentView.invoice', { number: ltrIn(a.refNumber) })}
                    </Link>
                  )}
                  <span className="tnum text-end">
                    {formatDate(a.date)} · {d.currency} {formatAmount(a.amount)}
                    {a.exchangeGainLoss ? (
                      <span className="block text-xs text-muted-foreground">
                        {t(
                          a.exchangeGainLoss.startsWith('-')
                            ? 'salesPages.paymentView.exchangeLoss'
                            : 'salesPages.paymentView.exchangeGain',
                          {
                            amount: `${d.homeCurrency} ${formatAmount(a.exchangeGainLoss.replace('-', ''))}`,
                          },
                        )}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
              <li className="flex justify-between px-4 py-2 font-medium">
                <span>{t('salesPages.paymentView.totalApplied')}</span>
                <span className="tnum">
                  {d.currency} {formatMinor(applied, exp)}
                </span>
              </li>
            </ul>
          ) : (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              {t('salesPages.paymentView.notApplied')}
            </p>
          )}
        </section>
      </div>
      <VoidDialog
        open={voiding}
        title={t('salesPages.paymentView.voidTitle', { number: ltrIn(d.number) })}
        description={t('salesPages.paymentView.voidBody')}
        minDate={d.date}
        pending={voidM.isPending}
        error={voidM.error ? voidM.error.message : null}
        onCancel={() => setVoiding(false)}
        onConfirm={(date, reason) => voidM.mutate({ date, reason })}
      />
    </OverlayPage>
  );
}
